import { makeAutoObservable, runInAction, computed } from "mobx";
import { surveysService } from "../services";

const DEFAULT_RESPONSES_PAGE_SIZE = 5;

const resolvePayload = (payload) => {
  if (payload && typeof payload === "object" && "data" in payload) {
    return payload.data;
  }
  return payload;
};

class SurveysStore {
  standardSurveys = [];
  customSurveys = [];
  categories = [];
  lookupsLoaded = false;
  responses = [];

  standardLoading = false;
  customLoading = false;
  lookupsLoading = false;
  responsesLoading = false;

  standardError = "";
  customError = "";
  responsesError = "";
  lookupsError = "";

  responsesPage = 1;
  responsesPageSize = DEFAULT_RESPONSES_PAGE_SIZE;
  responsesTotal = 0;
  responsesTotalPages = 1;

  constructor() {
    makeAutoObservable(this, {
      hasStandardSurveys: computed,
      hasCustomSurveys: computed,
      hasResponses: computed,
    });
  }

  get hasStandardSurveys() {
    return this.standardSurveys.length > 0;
  }

  get hasCustomSurveys() {
    return this.customSurveys.length > 0;
  }

  get hasResponses() {
    return this.responses.length > 0;
  }

  setResponsesPage = (page) => {
    this.responsesPage = page;
  };

  setResponsesPageSize = (size) => {
    this.responsesPageSize = size;
  };

  loadLookups = async () => {
    runInAction(() => {
      this.lookupsLoading = true;
      this.lookupsError = "";
    });

    try {
      const data = await surveysService.fetchLookups();

      const payload = resolvePayload(data) || {};
      runInAction(() => {
        this.categories = Array.isArray(payload.categories)
          ? payload.categories
          : [];
        this.lookupsLoaded = true;
      });
    } catch (error) {
      runInAction(() => {
        this.categories = [];
        this.lookupsError = error?.message || "Unable to load survey lookups.";
        this.lookupsLoaded = false;
      });
    } finally {
      runInAction(() => {
        this.lookupsLoading = false;
      });
    }
  };

  loadStandardSurveys = async () => {
    runInAction(() => {
      this.standardLoading = true;
      this.standardError = "";
    });

    try {
      const data = await surveysService.listStandard();

      const payload = resolvePayload(data) || {};
      const surveys = payload.surveys || payload.items || [];
      runInAction(() => {
        this.standardSurveys = Array.isArray(surveys) ? surveys : [];
      });
    } catch (error) {
      runInAction(() => {
        this.standardSurveys = [];
        this.standardError =
          error?.message || "Unable to load standard surveys.";
      });
    } finally {
      runInAction(() => {
        this.standardLoading = false;
      });
    }
  };

  loadCustomSurveys = async () => {
    runInAction(() => {
      this.customLoading = true;
      this.customError = "";
    });

    try {
      const data = await surveysService.listCustom();

      const payload = resolvePayload(data) || {};
      const surveys =
        payload.surveys ||
        payload.items ||
        payload.data ||
        payload.results ||
        [];
      runInAction(() => {
        this.customSurveys = Array.isArray(surveys) ? surveys : [];
      });
    } catch (error) {
      runInAction(() => {
        this.customSurveys = [];
        this.customError = error?.message || "Unable to load custom surveys.";
      });
    } finally {
      runInAction(() => {
        this.customLoading = false;
      });
    }
  };

  loadResponses = async (options = {}) => {
    const { page = this.responsesPage, limit = this.responsesPageSize } =
      options;

    runInAction(() => {
      this.responsesLoading = true;
      this.responsesError = "";
    });

    try {
      const data = await surveysService.listResponses({
        page,
        limit,
      });

      const payload = resolvePayload(data) || {};
      const responses = payload.responses || [];
      runInAction(() => {
        this.responses = Array.isArray(responses) ? responses : [];
        this.responsesPage = payload.page || page;
        this.responsesPageSize = payload.limit || limit;
        this.responsesTotal = payload.total || 0;
        this.responsesTotalPages = payload.totalPages || 1;
      });
    } catch (error) {
      runInAction(() => {
        this.responses = [];
        this.responsesTotal = 0;
        this.responsesTotalPages = 1;
        this.responsesError =
          error?.message || "Unable to load survey responses.";
      });
    } finally {
      runInAction(() => {
        this.responsesLoading = false;
      });
    }
  };
}

export const surveysStore = new SurveysStore();
